import type { ReactNode } from 'react';
import { AlertTriangle, Loader2, RotateCw } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ApiError } from '@/lib/api/client';
import { Button } from './button';

export function Spinner({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center justify-center py-10 text-muted', className)}>
      <Loader2 size={22} className="animate-spin" />
    </div>
  );
}

/** A grey block the size of what is coming. */
export function Skeleton({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <div style={style} className={cn('animate-pulse rounded-lg bg-surface-3', className)} />;
}

/** N placeholder rows for a table that is loading. */
export function SkeletonRows({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="divide-y divide-line rounded-2xl border border-line bg-surface">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-6 px-4 py-4">
          {Array.from({ length: cols }).map((_, j) => (
            <Skeleton key={j} className={cn('h-3.5', j === 0 ? 'w-48' : 'w-24')} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ icon, title, description, action }: { icon: ReactNode; title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-2 text-faint">{icon}</div>
      <p className="text-sm font-semibold text-ink">{title}</p>
      {description && <p className="max-w-md text-xs leading-5 text-muted">{description}</p>}
      {action}
    </div>
  );
}

type Tone = 'ok' | 'warn' | 'danger' | 'neutral' | 'accent';

const TONE: Record<Tone, string> = {
  ok:      'bg-ok-soft text-ok',
  warn:    'bg-warn-soft text-warn',
  danger:  'bg-danger-soft text-danger',
  neutral: 'bg-surface-2 text-muted',
  accent:  'bg-accent-soft text-accent-ink',
};

/** A callout with its own colour: for facts the reader must not scroll past. */
export function Notice({ tone, icon, title, children, className, action }: {
  tone: Tone;
  icon?: ReactNode;
  title?: string;
  children?: ReactNode;
  className?: string;
  action?: ReactNode;
}) {
  return (
    <div className={cn('flex items-start gap-3 rounded-xl p-4 text-sm', TONE[tone], className)}>
      <span className="mt-0.5 shrink-0">{icon ?? <AlertTriangle size={16} />}</span>
      <div className="min-w-0 flex-1 space-y-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className="text-xs leading-5 opacity-90">{children}</div>}
      </div>
      {action}
    </div>
  );
}

/** The failure of a whole page's query: the message, the request id to send the backend team, and a retry. */
export function QueryError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : String(error);
  const requestId = ApiError.is(error) ? error.requestId : undefined;
  const status = ApiError.is(error) ? error.statusCode : undefined;
  return (
    <Notice
      tone="danger"
      title={status === 403 ? 'Бэкенд отказал в доступе' : 'Не удалось загрузить'}
      action={onRetry && <Button size="sm" variant="outline" icon={<RotateCw size={13} />} onClick={onRetry}>Повторить</Button>}
    >
      {message}
      {requestId && <span className="mt-1 block font-mono text-[11px]">requestId: {requestId}</span>}
    </Notice>
  );
}
